// Planning Chauffeurs — Timeline Gantt 24h scrollable, tap-first
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAdminApi } from '../hooks/useAdminApi';
import {
  Calendar, Clock, ChevronLeft, ChevronRight, ArrowLeft, Filter, FileSpreadsheet,
  Loader2, X, MapPin, Package, Truck, AlertTriangle, Eye, Split
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import SplitStopModal from '../components/SplitStopModal';

// ------------------ HELPERS ------------------
const HOURS_24 = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

const todayIso = () => new Date().toISOString().slice(0, 10);
const addDays = (isoDate, delta) => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
};
const formatDayLabel = (isoDate) => {
  const d = new Date(isoDate + 'T12:00:00');
  return d.toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

// Convertit "HH:MM" → décimal (14:30 → 14.5)
const timeToDecimal = (timeStr) => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  return h + (m || 0) / 60;
};

// Nouvelle logique de couleurs du Planning basée sur l'ACTION EN COURS :
// - cueillette en cours = orange
// - livraison en cours = vert
// - pause en cours = kaki
// - fin de trajet = turquoise
// - en route (entre 2 arrêts, ou tous stops actifs sont "envoyés/acceptés") = bleu
// - complété (tout terminé) = blanc
// - retour SAC = rouge, bloqué = brun (cas particuliers)
const planningBlockColor = (currentAction, isReturn, isBlocked, allComplete) => {
  if (isReturn) return 'bg-red-500/30 border-red-500/90 text-red-100';
  if (isBlocked) return 'bg-yellow-900/40 border-yellow-800/80 text-yellow-200'; // brun
  if (allComplete) return 'bg-slate-100 border-slate-200 text-slate-900';
  switch (currentAction) {
    case 'cueillette_en_cours': return 'bg-orange-500/30 border-orange-500/90 text-orange-100';
    case 'livraison_en_cours':  return 'bg-green-500/30 border-green-500/90 text-green-100';
    case 'pause_en_cours':      return 'bg-lime-800/40 border-lime-700/80 text-lime-100'; // kaki
    case 'fin_trajet':          return 'bg-teal-400/30 border-teal-400/90 text-teal-100'; // turquoise
    case 'en_route':
    default:                    return 'bg-blue-500/30 border-blue-500/90 text-blue-100';
  }
};

// Libellé humain de l'état d'un bloc (correspond à la légende visuelle)
const planningStateLabel = (currentAction, isReturn, isBlocked, allComplete) => {
  if (isReturn) return 'Retour SAC';
  if (isBlocked) return 'Groupé';
  if (allComplete) return 'Complété';
  switch (currentAction) {
    case 'cueillette_en_cours': return 'Cueillette';
    case 'livraison_en_cours':  return 'Livraison';
    case 'pause_en_cours':      return 'Pause';
    case 'fin_trajet':          return 'Fin trajet';
    case 'en_route':
    default:                    return 'En route';
  }
};

// Détermine l'action en cours d'un rapport à partir de ses stops.
// Règles :
// 1) Un stop avec arrival_time mais SANS departure_time = actuellement actif (peu importe block_status)
// 2) Un stop avec block_status='en_cours' = actif (fallback)
// 3) Aucun stop actif + rapport 'completed' → blanc "Complété"
// 4) Aucun stop actif + rapport draft/brouillon → bleu "En route" (entre 2 arrêts)
const computeCurrentAction = (stops, reportStatus) => {
  if (!stops || stops.length === 0) return { action: 'en_route', allComplete: false };

  // 1. Détecter le stop actif : arrivé mais pas encore parti (source de vérité principale)
  const inProgressByTimestamp = stops.find((s) => s.arrival_time && !s.departure_time);
  // 2. Fallback via block_status='en_cours'
  const inProgressByStatus = stops.find((s) => (s.block_status === 'en_cours' || s.status === 'en_cours'));
  const inProgress = inProgressByTimestamp || inProgressByStatus;

  if (inProgress) {
    const t = inProgress.stop_type;
    if (t === 'fin_trajet') return { action: 'fin_trajet', allComplete: false };
    if (t === 'pause')      return { action: 'pause_en_cours', allComplete: false };
    if (t === 'cueillette' || t === 'pickup') return { action: 'cueillette_en_cours', allComplete: false };
    if (t === 'livraison' || t === 'delivery') return { action: 'livraison_en_cours', allComplete: false };
    return { action: 'en_route', allComplete: false };
  }

  // Aucun stop en cours :
  // - Rapport fermé (completed/terminated) → blanc "Complété"
  // - Rapport encore ouvert (draft) → bleu "En route" (chauffeur entre 2 arrêts, ou en attente prochaine mission)
  const isReportClosed = reportStatus === 'completed' || reportStatus === 'terminated' || reportStatus === 'complete';
  return { action: 'en_route', allComplete: isReportClosed };
};

// Palette de couleurs selon type/statut du bloc (ancienne fonction — gardée pour la légende de la Grille)
const blockColor = (stopType, status) => {
  if (status === 'complete' || status === 'completed') return 'bg-slate-100 border-slate-200 text-slate-900';
  if (status === 'cancelled' || status === 'annule') return 'bg-red-500/15 border-red-500/50 text-red-300';
  if (status === 'retourne') return 'bg-red-500/30 border-red-500/90 text-red-100';
  if (status === 'en_cours' || status === 'in_progress') return 'bg-green-500/25 border-green-500/80 text-green-200';
  if (status === 'envoye') return 'bg-yellow-400/30 border-yellow-500/90 text-yellow-200';
  if (status === 'accepte') return 'bg-green-500/25 border-green-500/80 text-green-200';
  if (status === 'blocked') return 'bg-yellow-900/40 border-yellow-800/80 text-yellow-200';
  if (stopType === 'cueillette' || stopType === 'pickup') return 'bg-orange-500/25 border-orange-500/80 text-orange-200';
  if (stopType === 'livraison' || stopType === 'delivery') return 'bg-green-500/25 border-green-500/80 text-green-200';
  return 'bg-slate-500/25 border-slate-500/80 text-slate-200';
};

// Helpers : extraire l'heure décimale d'une timestamp ISO ou d'un "HH:MM"
const decimalFromIso = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  if (iso.includes('T')) {
    const t = iso.split('T')[1] || '';
    const [h, m] = t.split(':').map(Number);
    if (Number.isFinite(h)) return h + (m || 0) / 60;
  }
  return null;
};
const dayFromIso = (iso) => (typeof iso === 'string' && iso.includes('T') ? iso.split('T')[0] : (iso && iso.length === 10 ? iso : null));
const decimalToHHMM = (dec) => {
  if (!Number.isFinite(dec)) return '?';
  const h = Math.floor(dec);
  const m = Math.round((dec - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// Compare chronologique complète (date+heure) pour tri multi-jours
const timestampOf = (stop) => {
  const iso = stop.arrival_time || stop.departure_time;
  if (iso && typeof iso === 'string') return iso; // ex. "2026-09-02T16:15:00"
  const d = stop.scheduled_date || stop.required_date || '9999-12-31';
  const t = stop.required_time || '00:00';
  return `${d}T${t}:00`;
};

// Anchor d'un stop : jour effectif (arrival puis departure puis scheduled puis required)
const effectiveDay = (stop) => dayFromIso(stop.arrival_time) || dayFromIso(stop.departure_time) || stop.scheduled_date || stop.required_date || null;
// Heure effective de début d'un stop (pour rendu Gantt)
const effectiveStartHour = (stop) => decimalFromIso(stop.arrival_time) ?? decimalFromIso(stop.departure_time) ?? timeToDecimal(stop.required_time) ?? 8;
const effectiveEndHour = (stop) => decimalFromIso(stop.departure_time) ?? decimalFromIso(stop.arrival_time) ?? timeToDecimal(stop.required_time) ?? (effectiveStartHour(stop) + 1);

// Génère les blocs d'un chauffeur pour un jour donné.
// RÈGLE : 1 bloc = 1 RAPPORT (voyage). Tous les stops qui partagent le même `linked_report_id`
// sont regroupés en un seul bloc allant du 1er arrival_time au dernier departure_time.
// Les stops sans linked_report_id (planifiés mais pas encore exécutés, ou fin_trajet orphelins)
// deviennent chacun 1 bloc individuel — utile pour détecter les anomalies (rapport tronqué).
function buildBlocksForDriver(driverStops, targetDate) {
  // 1. Regrouper par linked_report_id (les null gardent leur identité individuelle)
  const groups = new Map();
  driverStops.forEach((s, idx) => {
    const key = s.linked_report_id || `__stop__${s.id || idx}`;
    if (!groups.has(key)) groups.set(key, { key, isReport: !!s.linked_report_id, stops: [] });
    groups.get(key).stops.push(s);
  });

  // Priorité de statut pour l'agrégation d'un rapport (le "pire"/plus significatif l'emporte)
  const statusPriority = { retourne: 5, blocked: 4, en_cours: 3, envoye: 2, accepte: 2, pending: 1, nouvelle: 1, complete: 0, completed: 0, cancelled: 0 };
  const aggregateStatus = (stops) => {
    let best = stops[0]?.block_status || stops[0]?.status || 'pending';
    let bestP = statusPriority[best] ?? 1;
    stops.forEach((s) => {
      const st = s.block_status || s.status || 'pending';
      const p = statusPriority[st] ?? 1;
      if (p > bestP) { best = st; bestP = p; }
    });
    return best;
  };

  const rawBlocks = [];
  let idx = 0;
  groups.forEach(({ isReport, stops }) => {
    // Trier les stops du groupe chronologiquement (date+heure complète pour multi-jours)
    stops.sort((a, b) => timestampOf(a).localeCompare(timestampOf(b)));
    const first = stops[0];
    const last = stops[stops.length - 1];

    // ─────────────────────────────────────────────────────
    // Bornes du bloc = SOURCE DE VÉRITÉ du rapport si dispo
    // (linked_report_start_time/end_time = les champs "Début/Fin" officiels du rapport)
    // Sinon, calcul depuis les stops réels du dispatch
    // ─────────────────────────────────────────────────────
    const reportStart = first.linked_report_start_time; // "HH:MM"
    const reportEnd = first.linked_report_end_time;
    const reportDate = first.linked_report_date; // "YYYY-MM-DD" de début officiel
    const reportEndDate = first.linked_report_end_date || reportDate;

    const parseHHMM = (str) => {
      if (!str || typeof str !== 'string') return null;
      const [h, m] = str.split(':').map(Number);
      return Number.isFinite(h) ? h + (m || 0) / 60 : null;
    };

    // Un rapport peut contenir cueillette + livraison → afficher les 2 types
    const stopTypes = [...new Set(stops.map((s) => s.stop_type).filter(Boolean))];
    const isFinTrajet = stopTypes.length === 1 && stopTypes[0] === 'fin_trajet';
    const isReturn = stops.some((s) => s.stop_type === 'retour_sac' || s.block_status === 'retourne');

    // Détection fin_trajet (par type OU par nom client — car parfois stop_type=livraison mais client=FIN DE TRAJET)
    const isFinTrajetStop = (s) => s.stop_type === 'fin_trajet' || (s.client_name || '').toUpperCase() === 'FIN DE TRAJET';
    // Stops réels (hors fin_trajet auto-créé) — utilisés pour client, code, heure de début ET de fin.
    // Le fin_trajet représente le retour au dépôt, pas le temps de travail sur les commandes.
    const realStops = stops.filter((s) => !isFinTrajetStop(s));
    const firstReal = realStops[0] || first;
    const lastReal = realStops[realStops.length - 1] || last;

    const start = effectiveStartHour(firstReal);
    const rawEnd = effectiveEndHour(lastReal);
    const arrivalDay = dayFromIso(firstReal.arrival_time);
    const departureDay = dayFromIso(lastReal.departure_time || lastReal.arrival_time);

    // Priorité aux bornes officielles du rapport (champs Début/Fin) si dispo
    const reportStartHour = parseHHMM(reportStart);
    const reportEndHour = parseHHMM(reportEnd);
    const effStart = reportStartHour ?? start;
    let effEnd = reportEndHour ?? rawEnd;
    const effArrivalDay = reportDate || arrivalDay;
    let effDepartureDay = reportEndDate || departureDay;

    // Si un stop "fin_trajet" existe APRÈS le end_time officiel du rapport,
    // étirer le bloc jusqu'à ce stop (le rapport n'a pas été correctement clôturé
    // au moment du retour au dépôt — sinon on aurait un "trou" visuel dans le planning)
    const finTrajetStops = stops.filter(isFinTrajetStop);
    if (finTrajetStops.length > 0) {
      const latestFinTrajetHour = Math.max(
        ...finTrajetStops.map((s) => effectiveEndHour(s)).filter((h) => Number.isFinite(h))
      );
      const latestFinTrajetDay = finTrajetStops
        .map((s) => dayFromIso(s.departure_time || s.arrival_time))
        .filter(Boolean)
        .sort()
        .pop();
      if (Number.isFinite(latestFinTrajetHour) && latestFinTrajetHour > effEnd) {
        effEnd = latestFinTrajetHour;
        if (latestFinTrajetDay) effDepartureDay = latestFinTrajetDay;
      }
    }

    // Si le rapport est encore ouvert (draft/in_progress) ET la date cible est aujourd'hui,
    // étirer le bloc jusqu'à l'heure actuelle (le chauffeur roule toujours vers le terminal)
    const reportStatusVal = firstReal.linked_report_status || first.linked_report_status;
    const isReportOpen = reportStatusVal === 'draft' || reportStatusVal === 'in_progress';
    const isTargetToday = targetDate === todayIso();
    if (isReportOpen && isTargetToday) {
      const now = new Date();
      const nowHour = now.getHours() + now.getMinutes() / 60;
      if (nowHour > effEnd) {
        effEnd = nowHour;
        effDepartureDay = targetDate;
      }
    }

    const startCol = (effArrivalDay && effArrivalDay < targetDate) ? 0 : Math.max(0, Math.min(24, effStart));
    const endCol = (effDepartureDay && effDepartureDay > targetDate) ? 24 : Math.max(startCol + 0.5, Math.min(24, effEnd));
    const span = Math.max(0.5, endCol - startCol);

    // Client : ignorer fin_trajet (marqueur auto), prendre le 1er client réel
    const realClients = [...new Set(
      stops
        .filter((s) => s.stop_type !== 'fin_trajet')
        .map((s) => s.client_name)
        .filter(Boolean)
    )];
    const client = realClients[0] || first.client_name || first.return_reason || 'Sans client';
    const clientLabel = realClients.length > 1 ? `${client} +${realClients.length - 1}` : client;

    // Code affiché : priorité au numéro de rapport (RJ-YYYY-XXXX), puis order_number, puis fallback
    const reportNumber = first.linked_report_number || realStops[0]?.linked_report_number || null;
    const code = reportNumber
      || firstReal.order_number
      || first.order_number
      || (first.order_id ? first.order_id.slice(0, 8) : (isReport ? 'RAPPORT' : '—'));

    // Type d'étiquette : si mixte (CUEILL + LIVR) → "VOYAGE", sinon type dominant
    let typeLabel;
    if (isFinTrajet) typeLabel = 'FIN';
    else if (isReturn) typeLabel = 'RETOUR';
    else if (stopTypes.length > 1) typeLabel = 'VOYAGE';
    else if (stopTypes[0] === 'cueillette' || stopTypes[0] === 'pickup') typeLabel = 'CUEILL.';
    else typeLabel = 'LIVR.';

    const fmt = (iso, fallback) => {
      if (iso && typeof iso === 'string' && iso.includes('T')) return iso.split('T')[1]?.slice(0, 5) || fallback;
      return fallback || null;
    };
    // Fallbacks robustes : préférer arrival/departure des stops "réels"
    const startLabel = reportStart
      || fmt(firstReal.arrival_time || firstReal.departure_time, firstReal.required_time)
      || decimalToHHMM(effectiveStartHour(firstReal));
    // Si un fin_trajet a étiré le bloc au-delà du end_time officiel du rapport,
    // afficher l'heure réelle de fin (fin_trajet) au lieu du end_time obsolète
    const finTrajetHours = finTrajetStops
      .map((s) => effectiveEndHour(s))
      .filter((h) => Number.isFinite(h));
    const latestFinTrajetHourForLabel = finTrajetHours.length > 0 ? Math.max(...finTrajetHours) : null;
    const reportEndHourVal = parseHHMM(reportEnd);
    const useFinTrajetLabel = latestFinTrajetHourForLabel !== null
      && reportEndHourVal !== null
      && latestFinTrajetHourForLabel > reportEndHourVal;
    const rawEndLabel = useFinTrajetLabel
      ? decimalToHHMM(latestFinTrajetHourForLabel)
      : (reportEnd
        || fmt(lastReal.departure_time || lastReal.arrival_time, null)
        || decimalToHHMM(effectiveEndHour(lastReal)));
    const endLabel = (isReportOpen && isTargetToday) ? 'en cours' : rawEndLabel;
    const timeLabel = endLabel && endLabel !== startLabel ? `${startLabel} → ${endLabel}` : (startLabel || '?');

    // Ville de livraison prioritaire (depuis les stops du rapport lié), sinon fallback stops dispatch
    const deliveryCity = firstReal.linked_report_delivery_city
      || realStops[0]?.linked_report_delivery_city
      || (() => {
          const deliveryStops = realStops.filter((s) => s.stop_type === 'livraison' || s.stop_type === 'delivery');
          return deliveryStops[deliveryStops.length - 1]?.city || null;
        })();
    // Bons de livraison depuis le rapport lié
    const deliveryNotes = firstReal.linked_report_delivery_notes || realStops[0]?.linked_report_delivery_notes || [];

    // Ville : origine → destination si multi-stops (ignorer fin_trajet qui est le retour dépôt)
    const cities = [...new Set(
      stops
        .filter((s) => s.stop_type !== 'fin_trajet')
        .map((s) => s.city)
        .filter(Boolean)
    )];
    const cityLabel = deliveryCity || (cities.length > 1 ? `${cities[0]} → ${cities[cities.length - 1]}` : (cities[0] || first.city || '?'));

    // Calcul de la couleur du bloc selon l'action en cours + statut du rapport
    const reportStatus = first.linked_report_status || null;
    const { action: currentAction, allComplete } = computeCurrentAction(stops, reportStatus);
    // Détecter un vrai blocage administratif (frozen) — pas le "blocked" par défaut à la création
    const isBlocked = stops.some((s) => (s.block_status || s.status) === 'frozen');

    // Anomalie : fin_trajet isolé sans linked_report_id = rapport probablement tronqué
    const isOrphanFinTrajet = !isReport
      && stops.length === 1
      && (stops[0].stop_type === 'fin_trajet' || (stops[0].client_name || '').toUpperCase() === 'FIN DE TRAJET');

    rawBlocks.push({
      id: `${isReport ? 'rpt' : 'stp'}-${stops[0].id || idx}`,
      orderId: first.order_id,
      stopId: first.id,
      reportId: first.linked_report_id || null,
      stops,
      startCol,
      span,
      client: clientLabel,
      code,
      city: cityLabel,
      type: typeLabel,
      status: aggregateStatus(stops),
      currentAction,
      allComplete,
      isBlocked,
      isOrphanFinTrajet,
      stopType: first.stop_type,
      isFinTrajet,
      isReport,
      stopCount: stops.length,
      timeLabel,
      deliveryNotes,
      deliveryCity: deliveryCity || cityLabel,
      continuesLeft: (effArrivalDay && effArrivalDay < targetDate),
      continuesRight: (effDepartureDay && effDepartureDay > targetDate),
    });
    idx++;
  });

  // Trier par début pour lanes
  rawBlocks.sort((a, b) => a.startCol - b.startCol);

  // Système de lanes (sécurité — devrait rarement s'appliquer avec le regroupement par rapport)
  const laneEnds = [];
  rawBlocks.forEach((b) => {
    if (b.isFinTrajet) { b.laneIdx = 0; b.laneCount = 1; return; }
    let assigned = -1;
    for (let i = 0; i < laneEnds.length; i++) {
      if (laneEnds[i] <= b.startCol) { assigned = i; break; }
    }
    if (assigned === -1) { assigned = laneEnds.length; laneEnds.push(b.startCol + b.span); }
    else { laneEnds[assigned] = b.startCol + b.span; }
    b.laneIdx = assigned;
  });
  rawBlocks.forEach((b) => {
    if (b.isFinTrajet) return;
    let maxLane = b.laneIdx;
    rawBlocks.forEach((o) => {
      if (o.isFinTrajet || o === b) return;
      const overlap = !(o.startCol + o.span <= b.startCol || o.startCol >= b.startCol + b.span);
      if (overlap) maxLane = Math.max(maxLane, o.laneIdx);
    });
    b.laneCount = maxLane + 1;
  });

  return rawBlocks;
}

// ============================================================
// MAIN PAGE
// ============================================================
const DriverPlanningPage = () => {
  const { get, post } = useAdminApi();
  const navigate = useNavigate();

  const [drivers, setDrivers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [boardStopsToday, setBoardStopsToday] = useState({}); // driver_id → [stops] pour targetDate (via /board)
  const [openReports, setOpenReports] = useState([]); // rapports draft du jour (peuvent être sans stops)
  const [loading, setLoading] = useState(true);
  const [targetDate, setTargetDate] = useState(todayIso());
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [splittingOrder, setSplittingOrder] = useState(null); // commande source pour "Diviser"
  const [preselectStopId, setPreselectStopId] = useState(null);
  const scrollRef = useRef(null);

  // Load data — /board donne la vue exacte du jour (via arrival_time/departure_time/scheduled_date/required_date)
  // /orders sert à repérer les arrêts assignés sur d'autres dates pour l'aide "+N autres jours"
  const fetchData = useCallback(async () => {
    const [driversRes, ordersRes, boardRes] = await Promise.all([
      get('/saas/dashboard/drivers'),
      get('/dispatch/orders'),
      get(`/dispatch/board?date=${targetDate}`),
    ]);
    if (driversRes.success) {
      // Exclure les chauffeurs suspendus (driver_status === 'suspended')
      const activeDrivers = (driversRes.data || []).filter((d) => d.driver_status !== 'suspended');
      setDrivers(activeDrivers);
    }
    if (ordersRes.success) setOrders(ordersRes.data.orders || []);
    if (boardRes.success) {
      setBoardStopsToday(boardRes.data.driver_stops || {});
      setOpenReports(boardRes.data.open_reports || []);
      // Enrichir la liste des chauffeurs avec ceux qui apparaissent dans le board (au cas où)
      const boardDrivers = boardRes.data.drivers || [];
      setDrivers((prev) => {
        const known = new Set(prev.map((d) => d.id));
        const extras = boardDrivers.filter((d) => !known.has(d.id)).map((d) => ({
          id: d.id,
          first_name: (d.name || '').split(' ')[0] || '',
          last_name: (d.name || '').split(' ').slice(1).join(' ') || '',
          email: d.email,
          vehicle_info: null,
        }));
        return extras.length ? [...prev, ...extras] : prev;
      });
    } else {
      toast.error('Erreur chargement du planning');
    }
    setLoading(false);
  }, [get, targetDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh Planning toutes les 15s (temps réel)
  useEffect(() => {
    const interval = setInterval(() => { fetchData(); }, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Handler "Diviser" — appelé depuis BlockDetailsModal
  const handleMoveStop = useCallback(async (stopId, targetOrderId) => {
    if (!splittingOrder) return false;
    const res = await post(`/dispatch/orders/${splittingOrder.id}/stops/${stopId}/move`, {
      target_order_id: targetOrderId || null,
    });
    if (res.success) {
      fetchData();
      return true;
    }
    toast.error(res.error || 'Erreur lors du déplacement');
    return false;
  }, [splittingOrder, post, fetchData]);

  const openSplitForBlock = useCallback((block, stopId = null) => {
    const src = orders.find((o) => o.id === block.orderId);
    if (!src) { toast.error('Commande introuvable'); return; }
    setPreselectStopId(stopId);
    setSplittingOrder(src);
    setSelectedBlock(null);
  }, [orders]);

  // Tous les stops assignés à travers /orders (used pour le compteur "autres dates")
  const stopsByDriverAll = useMemo(() => {
    const byDriver = {};
    orders.forEach((order) => {
      (order.stops || []).forEach((s) => {
        if (!s.assigned_driver_id) return;
        (byDriver[s.assigned_driver_id] = byDriver[s.assigned_driver_id] || []).push({
          ...s,
          order_number: order.order_number || order.id.slice(0, 8),
        });
      });
    });
    return byDriver;
  }, [orders]);

  // Enrichir les stops du board avec order_number depuis /orders
  const stopsTodayEnriched = useMemo(() => {
    const orderNumById = {};
    orders.forEach((o) => { orderNumById[o.id] = o.order_number || o.id.slice(0, 8); });
    const enriched = {};
    Object.entries(boardStopsToday).forEach(([driverId, stops]) => {
      enriched[driverId] = (stops || []).map((s) => ({
        ...s,
        // Arrêt d'une date passée jamais commencé : affiché aujourd'hui pour être ré-attribué
        ...(s.overdue ? { scheduled_date: targetDate } : {}),
        order_number: orderNumById[s.order_id] || (s.order_id || '').slice(0, 8),
      }));
    });
    return enriched;
  }, [boardStopsToday, orders, targetDate]);

  // Build blocks per driver for the target date — utilise le driver_stops de /board (source de vérité)
  const driversWithBlocks = useMemo(() => {
    return drivers.map((drv) => {
      const stopsToday = stopsTodayEnriched[drv.id] || [];
      let blocks = buildBlocksForDriver(stopsToday, targetDate);

      // Injecter les rapports draft du chauffeur qui n'ont AUCUN bloc généré
      // (rapport ouvert mais pas encore d'arrêt) → bloc virtuel "En route" bleu
      const driverOpenReports = openReports.filter((r) => r.driver_id === drv.id);
      const existingReportIds = new Set(blocks.map((b) => b.reportId).filter(Boolean));
      driverOpenReports.forEach((r) => {
        if (existingReportIds.has(r.id)) return;
        const parseHHMM = (str) => {
          if (!str || typeof str !== 'string') return null;
          const [h, m] = str.split(':').map(Number);
          return Number.isFinite(h) ? h + (m || 0) / 60 : null;
        };
        const startHour = parseHHMM(r.start_time);
        const endHour = parseHHMM(r.end_time);
        const startDate = r.date;
        const endDate = r.end_date || r.date;
        // Calcule startCol/endCol selon multi-jours
        const startCol = startDate < targetDate ? 0 : (startHour ?? 0);
        // Si rapport en cours et pas d'end_time → étendre jusqu'à l'heure actuelle si on est aujourd'hui
        const now = new Date();
        const nowHour = now.getHours() + now.getMinutes() / 60;
        const isTodayReal = targetDate === todayIso();
        const effEndHour = endHour ?? (isTodayReal && endDate === targetDate ? nowHour : 24);
        const endCol = endDate > targetDate ? 24 : Math.max(startCol + 0.5, Math.min(24, effEndHour));
        blocks.push({
          id: `openrpt-${r.id}`,
          orderId: null,
          stopId: null,
          reportId: r.id,
          stops: [],
          startCol,
          span: Math.max(0.5, endCol - startCol),
          client: 'Rapport ouvert',
          code: r.report_number || 'RAPPORT',
          city: '—',
          type: 'EN ROUTE',
          status: 'en_cours',
          currentAction: 'en_route',
          allComplete: false,
          isBlocked: false,
          isOrphanFinTrajet: false,
          stopType: null,
          isFinTrajet: false,
          isReport: true,
          stopCount: 0,
          timeLabel: `${r.start_time || '?'}${r.end_time ? ` → ${r.end_time}` : ' → en cours'}`,
          continuesLeft: startDate < targetDate,
          continuesRight: endDate > targetDate,
          laneIdx: 0,
          laneCount: 1,
        });
      });

      const stopsAll = stopsByDriverAll[drv.id] || [];
      const otherDates = new Set(
        stopsAll
          .map((s) => s.scheduled_date || s.required_date || (s.arrival_time || '').slice(0, 10))
          .filter((d) => d && d !== targetDate)
      );
      return { ...drv, blocks, stopsCount: stopsToday.length + driverOpenReports.length, stopsOtherDays: otherDates.size, otherDatesList: [...otherDates].sort() };
    });
  }, [drivers, stopsTodayEnriched, stopsByDriverAll, targetDate, openReports]);

  // Nombre total d'arrêts assignés (toutes dates confondues) — utile pour repérer les orphelins
  const totalAssignedStops = useMemo(
    () => Object.values(stopsByDriverAll).reduce((sum, arr) => sum + arr.length, 0),
    [stopsByDriverAll]
  );
  const totalStopsToday = driversWithBlocks.reduce((sum, d) => sum + d.stopsCount, 0);

  // Saute à la date du prochain arrêt de ce chauffeur (utile quand des commandes sont sur d'autres dates)
  const jumpToFirstStopOfDriver = useCallback((driver) => {
    const stops = stopsByDriverAll[driver.id] || [];
    const dates = stops
      .map((s) => s.scheduled_date || s.required_date || (s.arrival_time || '').slice(0, 10))
      .filter(Boolean)
      .sort();
    const today = todayIso();
    const nextDate = dates.find((d) => d >= today) || dates[dates.length - 1];
    if (nextDate && nextDate !== targetDate) {
      setTargetDate(nextDate);
      toast.success(`Date ajustée au ${nextDate}`);
    }
  }, [stopsByDriverAll, targetDate]);

  // Scroll to "now" on mount and when date changes to today
  useEffect(() => {
    if (targetDate === todayIso() && scrollRef.current) {
      const now = new Date();
      const nowHour = now.getHours() + now.getMinutes() / 60;
      // Timeline width min 1920px, position at (nowHour/24) - some offset to center
      const pct = Math.max(0, (nowHour - 3) / 24);
      scrollRef.current.scrollLeft = pct * 1920;
    }
  }, [targetDate, driversWithBlocks.length]);

  const scrollToNow = () => {
    if (scrollRef.current) {
      const now = new Date();
      const nowHour = now.getHours() + now.getMinutes() / 60;
      scrollRef.current.scrollTo({ left: Math.max(0, ((nowHour - 3) / 24) * 1920), behavior: 'smooth' });
    }
  };

  const isToday = targetDate === todayIso();
  const nowDecimal = useMemo(() => {
    const n = new Date();
    return n.getHours() + n.getMinutes() / 60;
  }, [/* recompute on render is fine */]);

  return (
    <div className="h-full min-h-screen w-full max-w-full overflow-x-hidden bg-slate-950 text-white flex flex-col font-sans" data-testid="driver-planning-page">
      {/* HEADER */}
      <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex flex-wrap items-center justify-between gap-3 sticky top-0 z-30">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => navigate('/admin/commandes')}
            className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg flex items-center gap-1 text-xs font-semibold min-h-11 px-3"
            data-testid="back-to-orders-btn"
          >
            <ArrowLeft size={14} /><span>Commandes</span>
          </button>
          <div className="h-5 w-px bg-slate-800" />
          <div>
            <h1 className="text-lg font-bold uppercase tracking-wide text-white leading-tight flex items-center gap-2">
              <Clock className="text-amber-500" size={18} /> Planning des Chauffeurs
            </h1>
            <p className="text-xs text-slate-400">Timeline 24h • {driversWithBlocks.length} chauffeur(s)</p>
          </div>
          <div className="flex items-center bg-slate-950 border border-slate-800 p-1 rounded-lg ml-2">
            <button onClick={() => navigate('/admin/commandes')} className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800" data-testid="tab-grid-btn">
              <FileSpreadsheet size={14} /> Grille Commandes
            </button>
            <button className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-500 text-black shadow-sm" data-testid="tab-planning-active">
              <Clock size={14} /> Planning
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={scrollToNow}
            className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/40 min-h-11"
            data-testid="scroll-to-now-btn"
          >
            🎯 Maintenant
          </button>
        </div>
      </div>

      {/* Date banner + Sélecteur */}
      <div className="bg-slate-900/80 border-b-2 border-amber-500/30 px-4 py-2.5 sticky top-[64px] z-20 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap" data-testid="date-selector">
          <button onClick={() => setTargetDate(addDays(targetDate, -1))} className="flex items-center gap-1 px-2.5 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-amber-300 border border-slate-700 text-xs font-semibold min-h-11" data-testid="prev-day-btn" title="Jour précédent">
            <ChevronLeft size={16} /> Précédent
          </button>
          <label className="flex items-center gap-2 px-3 py-2 bg-amber-500/15 border-2 border-amber-500/60 rounded-lg cursor-pointer hover:bg-amber-500/25 transition-colors min-h-11 shadow-md" htmlFor="planning-date-input">
            <Calendar size={16} className="text-amber-400" />
            <input
              id="planning-date-input"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="bg-transparent border-0 text-white text-sm font-bold focus:outline-none cursor-pointer"
              style={{ colorScheme: 'dark' }}
              data-testid="date-picker"
              title="Choisir une date"
            />
          </label>
          <button onClick={() => setTargetDate(addDays(targetDate, 1))} className="flex items-center gap-1 px-2.5 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-amber-300 border border-slate-700 text-xs font-semibold min-h-11" data-testid="next-day-btn" title="Jour suivant">
            Suivant <ChevronRight size={16} />
          </button>
          <button
            onClick={() => setTargetDate(todayIso())}
            className={`px-3 py-2 rounded-lg text-xs font-semibold border min-h-11 ${isToday ? 'bg-amber-500 text-black border-amber-500' : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'}`}
            data-testid="jump-today-btn"
          >
            {isToday ? '✓ Aujourd\'hui' : "→ Aujourd'hui"}
          </button>
        </div>
        <div className="text-xs text-slate-300 font-semibold capitalize flex items-center gap-2 flex-wrap">
          <span>{formatDayLabel(targetDate)}</span>
          <span className="px-2 py-0.5 rounded-full bg-slate-800 text-amber-300 text-[11px]">{totalStopsToday} arrêt(s)</span>
          {(totalAssignedStops - totalStopsToday) > 0 && (
            <span className="text-amber-400 font-normal normal-case text-[11px]" data-testid="stops-other-days-hint">
              💡 {totalAssignedStops - totalStopsToday} sur d'autres dates
            </span>
          )}
        </div>
      </div>

      {/* Légende des couleurs du Planning (basée sur l'action en cours) */}
      <div className="bg-slate-900/40 border-b border-slate-800 px-4 py-1.5 flex items-center gap-3 flex-wrap text-[10px] font-medium sticky top-[89px] z-20" data-testid="status-legend">
        <span className="text-slate-500 uppercase tracking-wider text-[9px] font-bold shrink-0">Légende</span>
        {[
          { action: 'cueillette_en_cours', label: 'Cueillette en cours' },
          { action: 'pause_en_cours', label: 'Pause' },
          { action: 'en_route', label: 'En route' },
          { action: 'livraison_en_cours', label: 'Livraison en cours' },
          { action: 'fin_trajet', label: 'Fin de trajet' },
        ].map(({ action, label }) => (
          <span key={action} className="flex items-center gap-1.5 whitespace-nowrap">
            <span className={`inline-block w-3 h-3 rounded border-2 ${planningBlockColor(action, false, false, false)}`} />
            <span className="text-slate-300">{label}</span>
          </span>
        ))}
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <span className={`inline-block w-3 h-3 rounded border-2 ${planningBlockColor('en_route', false, false, true)}`} />
          <span className="text-slate-300">Complété</span>
        </span>
        <span className="flex items-center gap-1.5 whitespace-nowrap ml-2 pl-2 border-l border-slate-700">
          <span className={`inline-block w-3 h-3 rounded border-2 ${planningBlockColor(null, true, false, false)}`} />
          <span className="text-slate-300">Retour SAC</span>
        </span>
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <span className={`inline-block w-3 h-3 rounded border-2 ${planningBlockColor(null, false, true, false)}`} />
          <span className="text-slate-300">Bloqué</span>
        </span>
        <span className="flex items-center gap-1.5 whitespace-nowrap ml-2 pl-2 border-l border-slate-700" title="Fin de trajet créé après la fermeture du rapport — le rapport a été tronqué">
          <span className="inline-block w-3 h-3 rounded border-2 bg-amber-500/40 border-amber-400/90 animate-pulse" />
          <span className="text-amber-300">⚠️ Rapport tronqué</span>
        </span>
      </div>

      {/* TIMELINE */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {loading ? (
          <div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="animate-spin mr-2" /> Chargement…</div>
        ) : (
          <div className="relative flex-1 min-w-0 overflow-hidden">
            {/* Ombres gradient indicatrices de scroll (gauche/droite) */}
            <div className="pointer-events-none absolute top-0 bottom-0 left-56 w-4 bg-gradient-to-r from-slate-950 to-transparent z-20" />
            <div className="pointer-events-none absolute top-0 bottom-0 right-0 w-4 bg-gradient-to-l from-slate-950 to-transparent z-20" />

            <div
              ref={scrollRef}
              className="w-full h-full overflow-x-scroll overflow-y-auto bg-slate-950 planning-scroll"
              onWheel={(e) => {
                if (e.deltaY !== 0 && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                  scrollRef.current.scrollLeft += e.deltaY;
                }
              }}
            >
            {/* Time header */}
            <div className="flex border-b border-slate-800 bg-slate-900 sticky top-0 z-20 text-xs font-mono">
              <div className="w-56 shrink-0 py-1.5 pl-2.5 pr-1 font-sans font-bold text-slate-400 uppercase tracking-wider text-[11px] border-r border-slate-800 sticky left-0 z-30 bg-slate-900 flex items-center justify-between gap-1">
                <span>Chauffeur & Camion</span>
                <div className="flex gap-0.5 shrink-0">
                  <button
                    onClick={() => scrollRef.current?.scrollBy({ left: -400, behavior: 'smooth' })}
                    className="w-6 h-6 rounded flex items-center justify-center text-slate-500 hover:text-amber-400 hover:bg-slate-800 transition-colors"
                    data-testid="scroll-left-btn"
                    title="Défiler la timeline vers la gauche"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    onClick={() => scrollRef.current?.scrollBy({ left: 400, behavior: 'smooth' })}
                    className="w-6 h-6 rounded flex items-center justify-center text-slate-500 hover:text-amber-400 hover:bg-slate-800 transition-colors"
                    data-testid="scroll-right-btn"
                    title="Défiler la timeline vers la droite"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
              <div className="flex min-w-[1920px]">
                {HOURS_24.map((time) => (
                  <div key={time} className="flex-1 text-center py-2 border-r border-slate-800/80 text-slate-400 font-semibold text-[11px] min-w-[80px]">
                    {time}
                  </div>
                ))}
              </div>
            </div>

            {/* Rows */}
            <div className="divide-y divide-slate-800/80 text-xs">
              {driversWithBlocks.length === 0 && (
                <div className="p-8 text-center text-slate-500">Aucun chauffeur.</div>
              )}
              {driversWithBlocks.map((driver) => (
                <DriverRow
                  key={driver.id}
                  driver={driver}
                  isToday={isToday}
                  nowDecimal={nowDecimal}
                  onBlockClick={(block) => setSelectedBlock({ block, driver })}
                  onJumpToFirstStop={jumpToFirstStopOfDriver}
                />
              ))}
            </div>
            </div>
          </div>
        )}
      </div>

      {selectedBlock && (
        <BlockDetailsModal
          block={selectedBlock.block}
          driver={selectedBlock.driver}
          onClose={() => setSelectedBlock(null)}
          onSplit={(stopId) => openSplitForBlock(selectedBlock.block, stopId)}
        />
      )}

      {splittingOrder && (
        <SplitStopModal
          sourceOrder={splittingOrder}
          allOrders={orders}
          preselectStopId={preselectStopId}
          onClose={() => { setSplittingOrder(null); setPreselectStopId(null); }}
          onMove={handleMoveStop}
        />
      )}
    </div>
  );
};

// ============================================================
// DRIVER ROW
// ============================================================
const DriverRow = ({ driver, isToday, nowDecimal, onBlockClick, onJumpToFirstStop }) => {
  const initials = `${(driver.first_name?.[0] || '?').toUpperCase()}${(driver.last_name?.[0] || '').toUpperCase()}`;
  // Hauteur de la row = base + lanes supplémentaires si chevauchement
  const maxLanes = driver.blocks.reduce((m, b) => Math.max(m, b.laneCount || 1), 1);
  const rowHeightPx = Math.max(64, 44 + maxLanes * 34);
  return (
    <div className="flex hover:bg-slate-900/40 group" style={{ minHeight: rowHeightPx }}>
      <div className="w-56 shrink-0 p-2.5 bg-slate-900/70 border-r border-slate-800 flex items-center justify-between sticky left-0 z-10">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center font-bold text-amber-500 shrink-0 text-xs">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="font-bold text-white text-xs truncate">
              {`${driver.first_name || ''} ${driver.last_name || ''}`.trim() || driver.email}
            </div>
            <p className="text-[11px] text-slate-400 truncate flex items-center gap-1">
              <Truck size={10} /> {driver.vehicle_info || 'Aucun camion'}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-0.5 shrink-0 ml-1">
          <span className="bg-slate-800 text-slate-300 font-mono text-[10px] px-2 py-0.5 rounded-full border border-slate-700">
            {driver.stopsCount}
          </span>
          {driver.stopsOtherDays > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); onJumpToFirstStop?.(driver); }}
              className="text-[9px] text-amber-400 hover:text-amber-300 hover:underline whitespace-nowrap"
              title="Sauter au jour du prochain arrêt de ce chauffeur"
              data-testid={`jump-other-days-${driver.id}`}
            >
              +{driver.stopsOtherDays} autres jours
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 relative min-w-[1920px] bg-slate-950 flex">
        {HOURS_24.map((time) => (<div key={time} className="flex-1 border-r border-slate-900/80 h-full pointer-events-none min-w-[80px]" />))}
        {isToday && (
          <div
            className="absolute top-0 bottom-0 z-10 w-0.5 bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)] pointer-events-none"
            style={{ left: `${(nowDecimal / 24) * 100}%` }}
            title={`Heure actuelle : ${String(Math.floor(nowDecimal)).padStart(2, '0')}:${String(Math.round((nowDecimal - Math.floor(nowDecimal)) * 60)).padStart(2, '0')}`}
          >
            <div className="w-2 h-2 rounded-full bg-red-500 -ml-[3px] -top-1 absolute" />
          </div>
        )}
        {driver.blocks.map((block) => {
          const leftPct = (block.startCol / 24) * 100;
          const widthPct = (block.span / 24) * 100;

          // Bloc positionné en lane (empilement vertical si chevauchement)
          const laneCount = Math.max(1, block.laneCount || 1);
          const laneIdx = block.laneIdx || 0;
          const topPct = (laneIdx / laneCount) * 100;
          const heightPct = (1 / laneCount) * 100;

          // Couleur = action en cours (fin_trajet = turquoise, cueillette = orange, etc.)
          const colorCls = block.isOrphanFinTrajet
            ? 'bg-amber-500/40 border-amber-400/90 text-amber-100 animate-pulse'
            : (block.isFinTrajet
              ? planningBlockColor('fin_trajet', false, false, false)
              : planningBlockColor(block.currentAction, block.status === 'retourne', block.isBlocked, block.allComplete));

          return (
            <button
              key={block.id}
              onClick={() => onBlockClick(block)}
              className={`absolute rounded-lg border-2 px-2 py-1 shadow-md flex flex-col justify-between overflow-hidden cursor-pointer hover:brightness-110 hover:z-20 transition-all text-left ${colorCls} ${block.stops?.some((st) => st.overdue) ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-slate-900' : ''}`}
              style={{
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                top: `calc(${topPct}% + 4px)`,
                height: `calc(${heightPct}% - 8px)`,
              }}
              title={block.isOrphanFinTrajet
                ? `⚠️ ANOMALIE : Fin de trajet ${block.timeLabel} créé sans être rattaché à un rapport — le chauffeur a probablement fermé son rapport trop tôt (avant de faire son retour au dépôt)`
                : `${block.code} — ${block.client} (${block.timeLabel})`}
              data-testid={`block-${block.id}`}
            >
              {/* Ligne 1 : [Code | Bons de livraison | Ville de livraison] */}
              <div className="flex items-center justify-between gap-1.5 leading-tight">
                <span className="font-bold font-mono text-[10px] shrink-0 flex items-center gap-0.5">
                  {block.stops?.some((st) => st.overdue) && (
                    <span className="text-[8px] font-bold bg-red-600 text-white px-1 rounded mr-0.5" title={`En retard — prévu le ${block.stops.find((st) => st.overdue)?.original_date}`} data-testid={`block-overdue-${block.id}`}>RETARD</span>
                  )}
                  {block.continuesLeft && <span className="text-white/80">←</span>}
                  {block.code}
                </span>
                <span className="font-semibold text-[11px] truncate flex-1 text-center font-sans" title={(block.deliveryNotes || []).join(', ') || block.client}>
                  {block.deliveryNotes && block.deliveryNotes.length > 0
                    ? (block.deliveryNotes.length <= 2 ? block.deliveryNotes.join(', ') : `${block.deliveryNotes.slice(0, 2).join(', ')} +${block.deliveryNotes.length - 2}`)
                    : block.client}
                </span>
                <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-black/40 shrink-0 flex items-center gap-0.5 max-w-[100px] truncate" title={block.deliveryCity}>
                  {block.deliveryCity || block.type}
                  {block.continuesRight && <span className="text-white/80">→</span>}
                </span>
              </div>
              {/* Ligne 2 : Client à gauche | Heures centrées en bas (badge absolu) | Type en bas à droite */}
              <div className="flex items-center gap-1.5 text-[10px] opacity-95 font-sans leading-tight relative">
                <span className="truncate flex items-center gap-0.5 min-w-0 flex-1">
                  <MapPin size={9} className="shrink-0" />
                  <span className="truncate">{block.client}</span>
                </span>
                <span
                  className="absolute left-1/2 -translate-x-1/2 font-mono font-semibold whitespace-nowrap bg-black/60 px-1.5 py-0.5 rounded text-[9px] pointer-events-none shadow-sm"
                  data-testid={`block-time-${block.id}`}
                >
                  {block.timeLabel}
                </span>
                <span
                  className="text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-black/50 shrink-0 whitespace-nowrap"
                  title={`État : ${planningStateLabel(block.currentAction, block.status === 'retourne', block.isBlocked, block.allComplete)}`}
                  data-testid={`block-state-${block.id}`}
                >
                  {planningStateLabel(block.currentAction, block.status === 'retourne', block.isBlocked, block.allComplete)}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================
// BLOCK DETAILS MODAL (tap on block)
// ============================================================
const BlockDetailsModal = ({ block, driver, onClose, onSplit }) => {
  const totalPallets = block.stops?.reduce((s, x) => s + (x.items?.length || 0), 0) || 0;
  const canSplit = (block.stops?.length || 0) >= 2;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="block-details-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-t-2xl sm:rounded-xl w-full sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="text-white font-bold text-base truncate flex items-center gap-2">
              <span className="text-amber-500 font-mono">{block.code}</span>
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-slate-800 text-slate-300">{block.type}</span>
            </h3>
            <p className="text-xs text-slate-400 mt-0.5 truncate">
              {`${driver.first_name || ''} ${driver.last_name || ''}`.trim()} • {block.timeLabel}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white min-h-11 min-w-11" data-testid="close-block-details"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3 text-xs">
          <InfoRow icon={MapPin} label="Client" value={block.client} />
          <InfoRow icon={MapPin} label="Trajet" value={block.city} />
          <InfoRow icon={Clock} label="Horaire" value={block.timeLabel} />
          <InfoRow icon={Package} label="Palettes totales" value={totalPallets} />
          <div className="pt-2 border-t border-slate-800">
            <p className="text-slate-400 mb-2 font-semibold text-[11px] uppercase tracking-wider">Arrêts ({block.stops?.length || 0})</p>
            <div className="space-y-1.5">
              {block.stops?.map((stop, i) => (
                <div key={stop.id || i} className="bg-slate-800/60 rounded-lg p-2 border border-slate-700 flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                    (stop.stop_type === 'cueillette' || stop.stop_type === 'pickup') ? 'bg-blue-500/30 text-blue-300' : 'bg-cyan-500/30 text-cyan-300'
                  }`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold truncate">{stop.client_name || '—'}</div>
                    <div className="text-slate-400 truncate">
                      {(stop.stop_type === 'cueillette' || stop.stop_type === 'pickup') ? '📤 Cueillette' : '📥 Livraison'} • {stop.city || '?'} • {stop.required_time || '?'}
                    </div>
                  </div>
                  {canSplit && onSplit && (
                    <button
                      onClick={() => onSplit(stop.id)}
                      className="p-1.5 rounded text-slate-400 hover:text-amber-400 hover:bg-slate-700 min-h-9 min-w-9 shrink-0"
                      data-testid={`block-stop-split-${stop.id}`}
                      title="Déplacer cet arrêt vers une autre commande"
                    >
                      <Split size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="p-3 border-t border-slate-800 flex gap-2">
          {onSplit && (
            <button
              onClick={canSplit ? () => onSplit(null) : undefined}
              disabled={!canSplit}
              title={canSplit ? 'Déplacer un arrêt vers une autre commande' : 'La commande doit avoir au moins 2 arrêts'}
              className={`flex-1 py-2.5 rounded-lg text-xs font-semibold min-h-11 flex items-center justify-center gap-2 ${
                canSplit
                  ? 'bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300'
                  : 'bg-slate-800/50 text-slate-600 cursor-not-allowed'
              }`}
              data-testid="block-split-btn"
            >
              <Split size={13} /> Diviser
            </button>
          )}
          <button
            onClick={() => { onClose(); /* futur: naviguer à modification */ }}
            className="flex-1 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold min-h-11 flex items-center justify-center gap-2"
            data-testid="block-modify-btn"
          >
            <Eye size={13} /> Voir dans Grille
          </button>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 text-slate-300">
    <Icon size={13} className="text-amber-500 shrink-0" />
    <span className="text-slate-400 min-w-20">{label} :</span>
    <span className="font-semibold text-white truncate">{value || '—'}</span>
  </div>
);

export default DriverPlanningPage;
